// Generate a 1080×1920 (Instagram Story) affirmation image on the client
// using an offscreen canvas, then invoke navigator.share({ files }) if
// available. Falls back to opening/downloading the PNG if not.
//
// Design:
//   • Full-bleed theme photo at 55% opacity with a color gradient over it
//   • Concentric guide rings top-right (signature look)
//   • Playfair title, italic body first sentence, watermark
//
// The Unsplash CDN sends CORS headers so we can safely read the pixels.

const WM = 'reflectionsbyrowdy.com';

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const wrapText = (ctx, text, maxWidth) => {
  const words = text.split(' ');
  const lines = [];
  let line = '';
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
};

export async function buildAffirmationImage({ theme, affirmation, image, eyebrow }) {
  const W = 1080;
  const H = 1920;

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  // 1) Base gradient (in case image fails to load)
  const grad = ctx.createLinearGradient(0, 0, W, H);
  grad.addColorStop(0, theme.gradient[0]);
  grad.addColorStop(1, theme.gradient[1]);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  // 2) Photo — best-effort, wrapped in try so the share still works if CORS fails
  if (image) {
    try {
      const img = await loadImage(image);
      // Fit-cover into full canvas
      const scale = Math.max(W / img.width, H / img.height);
      const dw = img.width * scale;
      const dh = img.height * scale;
      const dx = (W - dw) / 2;
      const dy = (H - dh) / 2;
      ctx.globalAlpha = 0.55;
      ctx.drawImage(img, dx, dy, dw, dh);
      ctx.globalAlpha = 1;
    } catch (_) { /* fall through — gradient alone still looks good */ }
  }

  // 3) Palette-tinted overlay for legibility (bottom-heavy)
  const overlay = ctx.createLinearGradient(0, H * 0.15, 0, H);
  overlay.addColorStop(0, `${theme.gradient[0]}66`);
  overlay.addColorStop(1, `${theme.gradient[1]}f0`);
  ctx.fillStyle = overlay;
  ctx.fillRect(0, 0, W, H);

  // 4) Concentric rings top-right (signature look)
  ctx.save();
  ctx.strokeStyle = 'rgba(255,255,255,0.22)';
  ctx.lineWidth = 2;
  for (const r of [340, 240, 160, 92]) {
    ctx.beginPath();
    ctx.arc(W - 60, 80, r, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.restore();

  // 5) Small gold halo bottom-left (echoes the sage-gold app glow)
  const halo = ctx.createRadialGradient(140, H - 260, 20, 140, H - 260, 400);
  halo.addColorStop(0, 'rgba(201,168,102,0.35)');
  halo.addColorStop(1, 'rgba(201,168,102,0)');
  ctx.fillStyle = halo;
  ctx.beginPath();
  ctx.arc(140, H - 260, 400, 0, Math.PI * 2);
  ctx.fill();

  // 6) TEXT ---------------------------------------------------------------
  // Top eyebrow — use native canvas letterSpacing (supported in modern browsers)
  ctx.fillStyle = 'rgba(255,255,255,0.85)';
  ctx.font = '600 30px "Inter", system-ui, sans-serif';
  ctx.textBaseline = 'top';
  ctx.textAlign = 'left';
  try { ctx.letterSpacing = '8px'; } catch (_) { /* not supported → fine, spacing is baked into fontSize */ }
  const eyebrowText = (eyebrow || `Today's Manifest · ${theme.title}`).toUpperCase();
  ctx.fillText(eyebrowText, 80, 140);
  try { ctx.letterSpacing = '0px'; } catch (_) {}

  // Title — Playfair
  ctx.fillStyle = '#ffffff';
  ctx.font = '600 96px "Playfair Display", serif';
  const titleLines = wrapText(ctx, affirmation.title, W - 160);
  let titleY = 260;
  for (const l of titleLines) {
    ctx.fillText(l, 80, titleY);
    titleY += 108;
  }

  // First-sentence tagline — italic Playfair
  const first = affirmation.body.split('. ')[0] + '.';
  ctx.fillStyle = 'rgba(255,255,255,0.9)';
  ctx.font = 'italic 500 52px "Playfair Display", serif';
  const tagLines = wrapText(ctx, first, W - 160);
  let tagY = titleY + 30;
  for (const l of tagLines) {
    ctx.fillText(l, 80, tagY);
    tagY += 66;
  }

  // Body — full text, smaller
  ctx.fillStyle = 'rgba(255,255,255,0.92)';
  ctx.font = '400 40px "Inter", system-ui, sans-serif';
  const bodyLines = wrapText(ctx, affirmation.body, W - 160);
  let bodyY = tagY + 80;
  for (const l of bodyLines) {
    if (bodyY > H - 260) break; // leave room for watermark
    ctx.fillText(l, 80, bodyY);
    bodyY += 56;
  }

  // 7) Watermark
  ctx.fillStyle = 'rgba(255,255,255,0.7)';
  ctx.font = '600 28px "Inter", system-ui, sans-serif';
  try { ctx.letterSpacing = '6px'; } catch (_) {}
  ctx.fillText('REFLECTIONS BY ROWDY', 80, H - 160);
  try { ctx.letterSpacing = '0px'; } catch (_) {}
  ctx.fillStyle = '#e3d2a8';
  ctx.font = '500 30px "Inter", system-ui, sans-serif';
  ctx.fillText(WM, 80, H - 110);

  // Small gold dot before wordmark
  ctx.fillStyle = '#D6AD60';
  ctx.beginPath();
  ctx.arc(60, H - 145, 8, 0, Math.PI * 2);
  ctx.fill();

  // Return as blob
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('canvas.toBlob failed'))),
      'image/png',
      0.95,
    );
  });
}

export async function shareAffirmationImage({ theme, affirmation, image, eyebrow }) {
  const blob = await buildAffirmationImage({ theme, affirmation, image, eyebrow });
  const file = new File([blob], `reflections-${theme.id}-${Date.now()}.png`, { type: 'image/png' });

  // Prefer native share sheet (mobile) with file support
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({
        files: [file],
        title: `Today’s Manifest · ${theme.title}`,
        text: `“${affirmation.title}” — Reflections by Rowdy`,
      });
      return { shared: true, method: 'native' };
    } catch (err) {
      if (err && err.name === 'AbortError') return { shared: false, method: 'cancelled' };
      // fall through to download
    }
  }

  // Fallback: trigger a download
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = file.name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
  return { shared: true, method: 'download' };
}
